import { Button, Input } from "@ews-admin/global-design-system";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  EditManagerFormData,
  editManagerSchema,
} from "../../../schemas/admin.schema";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  clearError,
  selectManagersError,
  selectManagersLoading,
  updateManagerRequest,
} from "../../../store/reducers/managersSlice";
import { Manager } from "../../../types/manager.definition";
import {
  CivilityEnum,
  NotificationPrefEnum,
  RoleEnum,
} from "../../../types/user.definition";
import {
  dateInputToInstant,
  instantToDateInput,
} from "../../../utils/date-format-utils";
import { Modal } from "../Modal/Modal";

// ── Types ──────────────────────────────────────────────────────────────────

interface EditManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  manager: Manager;
  onSuccess: () => void;
}

// ── Component ──────────────────────────────────────────────────────────────

export const EditManagerModal: React.FC<EditManagerModalProps> = ({
  isOpen,
  onClose,
  manager,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const loading = useAppSelector(selectManagersLoading);
  const error = useAppSelector(selectManagersError);

  const submittedRef = useRef(false);
  const prevIsOpen = useRef(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isValid, isDirty },
  } = useForm<EditManagerFormData>({
    resolver: zodResolver(editManagerSchema(t)),
    mode: "onChange",
  });

  // Populate form only when modal transitions from closed → open
  useEffect(() => {
    if (!isOpen || prevIsOpen.current === isOpen) return;
    prevIsOpen.current = isOpen;
    submittedRef.current = false;
    dispatch(clearError());
    reset({
      civility: manager.civility ?? CivilityEnum.MR,
      firstName: manager.firstName ?? "",
      lastName: manager.lastName ?? "",
      phoneNumber: manager.phoneNumber ?? "",
      dateOfBirth: instantToDateInput(manager.dateOfBirth),
    });
  }, [isOpen, manager, dispatch, reset]);

  useEffect(() => {
    if (!isOpen) prevIsOpen.current = false;
  }, [isOpen]);

  const onSubmit = (data: EditManagerFormData) => {
    submittedRef.current = true;
    dispatch(
      updateManagerRequest({
        managerId: String(manager.id),
        data: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: manager.email,
          phoneNumber: data.phoneNumber,
          civility: data.civility,
          roleName: RoleEnum.MANAGER,
          photoUrl: manager.photoUrl,
          preferredNotificationMethods:
            manager.preferredNotificationMethods ?? [
              NotificationPrefEnum.EMAIL,
            ],
          // Omitted when blank: the API applies only the fields it receives, so
          // an empty box leaves a birth date the manager set themselves alone.
          ...(dateInputToInstant(data.dateOfBirth)
            ? { dateOfBirth: dateInputToInstant(data.dateOfBirth) }
            : {}),
        },
      })
    );
    onClose();
  };

  const isSaving = loading && submittedRef.current;

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !isSaving && onClose()}
      title={t("admin.managers.edit")}
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="p-6 space-y-4">
          {error && submittedRef.current && (
            <p className="text-sm text-red-600">
              {t("admin.managers.editModal.error")}
            </p>
          )}

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">
              {t("admin.managers.editModal.civility")}
            </label>
            <Controller
              name="civility"
              control={control}
              render={({ field }) => (
                <select
                  {...field}
                  className="px-3 py-2 w-full text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={CivilityEnum.MR}>{t("common.mr")}</option>
                  <option value={CivilityEnum.MRS}>{t("common.mrs")}</option>
                </select>
              )}
            />
            {errors.civility && (
              <p className="mt-1 text-sm text-red-600">
                {errors.civility.message}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Input
              label={t("admin.managers.editModal.firstName")}
              placeholder={t("admin.managers.editModal.firstNamePlaceholder")}
              required
              fullWidth
              error={errors.firstName?.message}
              {...register("firstName")}
            />
            <Input
              label={t("admin.managers.editModal.lastName")}
              placeholder={t("admin.managers.editModal.lastNamePlaceholder")}
              required
              fullWidth
              error={errors.lastName?.message}
              {...register("lastName")}
            />
          </div>

          <Input
            label={t("admin.managers.editModal.email")}
            value={manager.email}
            type="email"
            fullWidth
            readOnly
            className="text-gray-500 bg-gray-50 cursor-not-allowed"
          />

          <Input
            label={t("admin.managers.editModal.phoneNumber")}
            placeholder={t("admin.managers.editModal.phoneNumberPlaceholder")}
            type="tel"
            required
            fullWidth
            error={errors.phoneNumber?.message}
            {...register("phoneNumber")}
          />

          <Input
            label={t("admin.createManager.dateOfBirth")}
            type="date"
            fullWidth
            error={errors.dateOfBirth?.message}
            {...register("dateOfBirth")}
          />
        </div>

        <div className="flex gap-3 justify-end px-6 py-4 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isSaving}
          >
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={!isValid || !isDirty || isSaving}>
            {isSaving ? t("common.processing") : t("common.save")}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
